import React, { useState, useRef, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Type, ZoomIn, ZoomOut, RotateCcw, RotateCw, Save, X, Palette } from 'lucide-react';

const TextOverlayEditor = ({ 
  mockupBase64, 
  initialPosition,
  onSave, 
  onCancel 
}) => {
  const containerRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [position, setPosition] = useState(initialPosition || { 
    x: 50, y: 35, width: 40, height: 15, rotation: 0 
  });
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  
  // Text options
  const [text, setText] = useState('Your Text Here');
  const [fontFamily, setFontFamily] = useState('Arial Black');
  const [fontSize, setFontSize] = useState(48);
  const [textColor, setTextColor] = useState('#FFFFFF');
  const [strokeColor, setStrokeColor] = useState('#000000');
  const [hasStroke, setHasStroke] = useState(true);

  const fonts = [
    { value: 'TT Octosquares ExtraBold', label: 'TT Octosquares ExtraBold' },
    { value: 'Arial Black', label: 'Arial Black (Bold)' },
    { value: 'Impact', label: 'Impact' },
    { value: 'Arial', label: 'Arial' },
    { value: 'Helvetica', label: 'Helvetica' },
    { value: 'Times New Roman', label: 'Times New Roman' },
    { value: 'Georgia', label: 'Georgia' },
    { value: 'Verdana', label: 'Verdana' },
    { value: 'Courier New', label: 'Courier New' },
  ];

  const colors = [
    { value: '#FFFFFF', label: 'White' },
    { value: '#000000', label: 'Black' },
    { value: '#FFD700', label: 'Gold' },
    { value: '#FF0000', label: 'Red' },
    { value: '#0000FF', label: 'Blue' },
    { value: '#00FF00', label: 'Green' },
    { value: '#FFA500', label: 'Orange' },
    { value: '#800080', label: 'Purple' },
    { value: '#C0C0C0', label: 'Silver' },
    { value: '#1E40AF', label: 'Navy' },
  ];

  const handleMouseDown = (e) => {
    if (e.target.classList.contains('text-overlay')) {
      setIsDragging(true);
      setDragStart({ x: e.clientX, y: e.clientY });
      e.preventDefault();
    }
  };

  const handleMouseMove = (e) => {
    if (!isDragging || !containerRef.current) return;

    const container = containerRef.current.getBoundingClientRect();
    const deltaX = ((e.clientX - dragStart.x) / container.width) * 100;
    const deltaY = ((e.clientY - dragStart.y) / container.height) * 100;

    setPosition(prev => ({
      ...prev,
      x: Math.max(5, Math.min(95, prev.x + deltaX)),
      y: Math.max(5, Math.min(95, prev.y + deltaY))
    }));

    setDragStart({ x: e.clientX, y: e.clientY });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const adjustFontSize = (delta) => {
    setFontSize(prev => Math.max(12, Math.min(120, prev + delta)));
  };

  const adjustRotation = (delta) => {
    setPosition(prev => ({
      ...prev,
      rotation: ((prev.rotation || 0) + delta + 360) % 360
    }));
  };

  const resetAll = () => {
    setPosition({ x: 50, y: 35, width: 40, height: 15, rotation: 0 });
    setText('Your Text Here');
    setFontSize(48);
    setTextColor('#FFFFFF');
    setStrokeColor('#000000');
    setHasStroke(true);
  };

  const handleSave = () => {
    if (onSave) {
      onSave({
        text,
        fontFamily,
        fontSize,
        textColor,
        strokeColor,
        hasStroke,
        position
      });
    }
  };

  // Calculate preview font size based on container
  const previewFontSize = `${fontSize * 0.5}px`;

  return (
    <div 
      style={{ 
        background: '#1a1a2e', 
        borderRadius: '12px', 
        padding: '20px',
        color: 'white'
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <h3 style={{ fontSize: '18px', fontWeight: '600', margin: 0 }}>
          <Type className="w-5 h-5 inline mr-2" />
          Text Overlay Editor
        </h3>
        <Button variant="ghost" size="sm" onClick={onCancel} style={{ color: 'white' }}>
          <X className="w-4 h-4" />
        </Button>
      </div>

      {/* Text Input */}
      <div style={{ marginBottom: '16px' }}>
        <label style={{ display: 'block', fontSize: '14px', marginBottom: '8px', color: '#a0aec0' }}>
          Text Content
        </label>
        <Input
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Enter your text..."
          style={{ background: '#2d2d44', border: '1px solid #4a4a6a', color: 'white' }}
          data-testid="text-overlay-input"
        />
      </div>

      {/* Font & Style Options */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px', marginBottom: '16px' }}>
        <div>
          <label style={{ display: 'block', fontSize: '13px', marginBottom: '6px', color: '#a0aec0' }}>
            Font
          </label>
          <Select value={fontFamily} onValueChange={setFontFamily}>
            <SelectTrigger style={{ background: '#2d2d44', border: '1px solid #4a4a6a', color: 'white' }}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {fonts.map(font => (
                <SelectItem key={font.value} value={font.value}>{font.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        <div>
          <label style={{ display: 'block', fontSize: '13px', marginBottom: '6px', color: '#a0aec0' }}>
            Text Color
          </label>
          <Select value={textColor} onValueChange={setTextColor}>
            <SelectTrigger style={{ background: '#2d2d44', border: '1px solid #4a4a6a', color: 'white' }}>
              <SelectValue>
                <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ width: '16px', height: '16px', borderRadius: '4px', background: textColor, border: '1px solid #666' }}></span>
                  {colors.find(c => c.value === textColor)?.label || 'Custom'}
                </span>
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              {colors.map(color => (
                <SelectItem key={color.value} value={color.value}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ width: '16px', height: '16px', borderRadius: '4px', background: color.value, border: '1px solid #666' }}></span>
                    {color.label}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <label style={{ display: 'block', fontSize: '13px', marginBottom: '6px', color: '#a0aec0' }}>
            Outline Color
          </label>
          <Select value={strokeColor} onValueChange={setStrokeColor}>
            <SelectTrigger style={{ background: '#2d2d44', border: '1px solid #4a4a6a', color: 'white' }}>
              <SelectValue>
                <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ width: '16px', height: '16px', borderRadius: '4px', background: strokeColor, border: '1px solid #666' }}></span>
                  {colors.find(c => c.value === strokeColor)?.label || 'Custom'}
                </span>
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              {colors.map(color => (
                <SelectItem key={color.value} value={color.value}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ width: '16px', height: '16px', borderRadius: '4px', background: color.value, border: '1px solid #666' }}></span>
                    {color.label}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div style={{ display: 'flex', alignItems: 'end' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
            <input 
              type="checkbox" 
              checked={hasStroke} 
              onChange={(e) => setHasStroke(e.target.checked)}
              style={{ width: '18px', height: '18px' }}
            />
            <span style={{ fontSize: '13px', color: '#a0aec0' }}>Show Outline</span>
          </label>
        </div>
      </div>

      {/* Mockup Preview */}
      <div 
        ref={containerRef}
        style={{ 
          position: 'relative',
          width: '100%',
          maxWidth: '500px',
          margin: '0 auto 16px',
          background: '#2d2d44',
          borderRadius: '8px',
          overflow: 'hidden',
          cursor: isDragging ? 'grabbing' : 'default'
        }}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
      >
        <img 
          src={`data:image/png;base64,${mockupBase64}`}
          alt="Mockup"
          style={{ width: '100%', display: 'block', pointerEvents: 'none' }}
          draggable={false}
        />

        {/* Text Overlay */}
        <div
          className="text-overlay"
          style={{
            position: 'absolute',
            left: `${position.x}%`,
            top: `${position.y}%`,
            transform: `translate(-50%, -50%) rotate(${position.rotation || 0}deg)`,
            cursor: isDragging ? 'grabbing' : 'grab',
            padding: '8px 16px',
            fontFamily: fontFamily,
            fontSize: previewFontSize,
            fontWeight: 'bold',
            color: textColor,
            textShadow: hasStroke ? `
              -2px -2px 0 ${strokeColor},
              2px -2px 0 ${strokeColor},
              -2px 2px 0 ${strokeColor},
              2px 2px 0 ${strokeColor},
              0 -2px 0 ${strokeColor},
              0 2px 0 ${strokeColor},
              -2px 0 0 ${strokeColor},
              2px 0 0 ${strokeColor}
            ` : 'none',
            whiteSpace: 'nowrap',
            userSelect: 'none',
            border: '2px dashed rgba(102, 126, 234, 0.5)',
            borderRadius: '4px'
          }}
          onMouseDown={handleMouseDown}
        >
          {text || 'Your Text Here'}
        </div>

        {/* Position Indicator */}
        <div style={{
          position: 'absolute',
          bottom: '8px',
          left: '8px',
          background: 'rgba(0,0,0,0.7)',
          padding: '4px 8px',
          borderRadius: '4px',
          fontSize: '11px'
        }}>
          X: {position.x.toFixed(0)}% | Y: {position.y.toFixed(0)}% | Size: {fontSize}px | Rot: {(position.rotation || 0).toFixed(0)}°
        </div>
      </div>

      {/* Size & Rotation Controls */}
      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', justifyContent: 'center', marginBottom: '16px' }}>
        <Button 
          variant="outline" 
          size="sm" 
          onClick={() => adjustFontSize(-4)}
          style={{ background: '#2d2d44', borderColor: '#4a4a6a', color: 'white' }}
        >
          <ZoomOut className="w-4 h-4 mr-1" /> Smaller
        </Button>
        <Button 
          variant="outline" 
          size="sm" 
          onClick={() => adjustFontSize(4)}
          style={{ background: '#2d2d44', borderColor: '#4a4a6a', color: 'white' }}
        >
          <ZoomIn className="w-4 h-4 mr-1" /> Larger
        </Button>
        <Button 
          variant="outline" 
          size="sm" 
          onClick={() => adjustRotation(-15)}
          style={{ background: '#2d2d44', borderColor: '#48bb78', color: '#48bb78' }}
        >
          <RotateCcw className="w-4 h-4 mr-1" /> -15°
        </Button>
        <Button 
          variant="outline" 
          size="sm" 
          onClick={() => adjustRotation(15)}
          style={{ background: '#2d2d44', borderColor: '#48bb78', color: '#48bb78' }}
        >
          <RotateCw className="w-4 h-4 mr-1" /> +15°
        </Button>
        <Button 
          variant="outline" 
          size="sm" 
          onClick={resetAll}
          style={{ background: '#2d2d44', borderColor: '#4a4a6a', color: 'white' }}
        >
          Reset All
        </Button>
      </div>

      {/* Quick Position Presets */}
      <div style={{ marginBottom: '20px' }}>
        <p style={{ fontSize: '13px', color: '#a0aec0', marginBottom: '8px' }}>Quick Positions:</p>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => setPosition({ x: 50, y: 35, width: 40, height: 15, rotation: 0 })}
            style={{ background: '#2d2d44', borderColor: '#4a4a6a', color: 'white', fontSize: '12px' }}
          >
            Center Chest
          </Button>
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => setPosition({ x: 50, y: 50, width: 60, height: 20, rotation: 0 })}
            style={{ background: '#2d2d44', borderColor: '#4a4a6a', color: 'white', fontSize: '12px' }}
          >
            Full Back
          </Button>
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => { setPosition({ x: 50, y: 38, width: 40, height: 15, rotation: -10 }); setFontSize(24); }}
            style={{ background: '#2d2d44', borderColor: '#48bb78', color: '#48bb78', fontSize: '12px' }}
          >
            Arched Text
          </Button>
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => { setPosition({ x: 50, y: 75, width: 30, height: 10, rotation: 0 }); setFontSize(24); }}
            style={{ background: '#2d2d44', borderColor: '#4a4a6a', color: 'white', fontSize: '12px' }}
          >
            Bottom
          </Button>
        </div>
      </div>

      {/* Action Buttons */}
      <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
        <Button variant="outline" onClick={onCancel} style={{ borderColor: '#4a4a6a', color: 'white' }}>
          Cancel
        </Button>
        <Button 
          onClick={handleSave}
          disabled={!text.trim()}
          style={{ background: '#667eea', color: 'white' }}
          data-testid="apply-text-overlay-btn"
        >
          <Save className="w-4 h-4 mr-2" />
          Apply Text
        </Button>
      </div>
    </div>
  );
};

export default TextOverlayEditor;
